import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { TradeMilestoneDefaults } from "./TradeMilestoneDefaults";

const SRC = { duty: ["任务职责", "duty"], rfq_work: ["RFQ 工作节点", "RFQ work"], rfq: ["RFQ 触发", "RFQ"], title: ["标题关键词", "title"] };

/* 全局默认：工种 ↔ 任务节点 + 里程碑默认完成条件（admin / ceo / eng_manager 可改；项目级覆盖优先） */
export const TradeTaskMapDialog = ({ api, open, onClose, zh }) => {
  const [d, setD] = useState(null);
  const [trade, setTrade] = useState("framing");
  const [map, setMap] = useState({});
  const [md, setMd] = useState({});
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setMap({}); setMd({}); setQ("");
    api.get("/settings/trade-task-map").then(({ data }) => { setD(data); if (!data.trades?.[trade]) setTrade(Object.keys(data.trades || {})[0] || ""); }).catch((e) => toast.error(e?.detailText || "Load failed"));
  }, [open, api]); // eslint-disable-line
  const eff = (r) => (map[r.node] !== undefined ? (map[r.node] || []) : (r.override ?? r.inferred));
  const toggle = (r) => { const cur = eff(r); setMap((m) => ({ ...m, [r.node]: cur.includes(trade) ? cur.filter((x) => x !== trade) : [...cur, trade] })); };
  const reset = (r) => setMap((m) => ({ ...m, [r.node]: null }));
  const dirty = Object.keys(map).length + Object.keys(md).length;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/trade-task-map", { map, milestoneDefaults: md });
      toast.success(zh ? `已保存全局默认（${Object.keys(data.map).length} 个任务覆盖 · ${(data.defaultsOverridden || []).length} 个工种自定义完成条件）` : "Saved");
      setMap({}); setMd({});
      setD((x) => ({ ...x, defaults: data.defaults, defaultsOverridden: data.defaultsOverridden || [], rows: x.rows.map((r) => ({ ...r, override: data.map[r.node] !== undefined ? data.map[r.node] : null })) }));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const groups = useMemo(() => {
    const out = [];
    const low = q.trim().toLowerCase();
    (d?.rows || []).filter((r) => !low || r.node.toLowerCase().includes(low) || (r.title || "").toLowerCase().includes(low) || (r.titleEn || "").toLowerCase().includes(low)).forEach((r) => {
      let g = out.find((x) => x.sub === r.sub);
      if (!g) { g = { sub: r.sub, subName: r.subName, rows: [] }; out.push(g); }
      g.rows.push(r);
    });
    return out;
  }, [d, q]);
  const srcLabel = (r) => (map[r.node] !== undefined ? (zh ? "本次修改" : "edited") : r.override != null ? (zh ? "全局覆盖" : "global") : r.source ? (zh ? SRC[r.source]?.[0] || "自动" : SRC[r.source]?.[1] || "auto") : "—");
  const scopeRows = d ? d.rows.filter((r) => eff(r).includes(trade)) : [];
  const inScope = scopeRows.length;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="trade-task-map-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">{zh ? "工种 ↔ 任务 映射 · 里程碑默认完成条件（全局默认）" : "Trade ↔ task map · milestone defaults (global)"}</DialogTitle>
          <DialogDescription className="text-[11px]">{zh ? "决定分包合同自动关联哪些任务链节点（分包卡「阶段任务链」、分包商工作台「我的工作安排」、任务下方「分包商卡」）。自动推断：任务职责「协同：xxx subcontractor」 > RFQ 映射 > 标题关键词；此处勾选为全局覆盖；项目内「调整范围」优先于全局。" : "Which task-chain nodes each trade's subcontract is linked to. Project-level scope overrides this global default."}</DialogDescription>
        </DialogHeader>
        {!d ? <p className="text-xs text-slate-400">…</p> : (
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2 flex-wrap">
              <select value={trade} onChange={(e) => setTrade(e.target.value)} className="h-8 rounded border border-slate-300 px-2" data-testid="trade-task-map-trade">
                {Object.entries(d.trades || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <span className="text-slate-500" data-testid="trade-task-map-count">{zh ? `该工种关联 ${inScope} 个任务` : `${inScope} tasks linked`}</span>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={zh ? "搜索节点 / 标题" : "Search"} className="h-8 rounded border border-slate-300 px-2 ml-auto w-48" data-testid="trade-task-map-search" />
            </div>
            <TradeMilestoneDefaults d={d} trade={trade} md={md} setMd={setMd} scopeRows={scopeRows} zh={zh} />
            <div className="max-h-[42vh] overflow-y-auto space-y-2" data-testid="trade-task-map-list">
              {groups.map((g) => (
                <div key={g.sub}>
                  <p className="text-[10px] font-semibold text-slate-500 mb-0.5">{g.sub} · {g.subName}</p>
                  {g.rows.map((r) => {
                    const on = eff(r).includes(trade);
                    const others = eff(r).filter((x) => x !== trade);
                    return (
                      <div key={r.node} className={`flex items-center gap-2 rounded px-2 py-1 ${on ? "bg-brand-50/60" : ""}`} data-testid={`trade-task-map-row-${r.node}`} data-on={on ? 1 : 0}>
                        <input type="checkbox" checked={on} disabled={!d.canEdit} onChange={() => toggle(r)} data-testid={`trade-task-map-cb-${r.node}`} />
                        <span className="font-mono text-[10px] text-slate-400">{r.node}</span>
                        <span className="flex-1 truncate text-slate-800" title={r.title}>{zh ? r.title : (r.titleEn || r.title)}</span>
                        <span className="text-[9px] text-slate-400">{r.roleZh}</span>
                        {others.length > 0 && <span className="text-[9px] text-slate-400 truncate max-w-[120px]" title={others.join(", ")}>{others.map((k) => d.trades[k] || k).join(", ")}</span>}
                        <span className="text-[9px] text-slate-400 w-16 text-right" data-testid={`trade-task-map-src-${r.node}`}>{srcLabel(r)}</span>
                        {d.canEdit && (r.override != null || map[r.node] !== undefined) && <button type="button" onClick={() => reset(r)} className="text-[9px] text-brand-600 hover:underline" data-testid={`trade-task-map-reset-${r.node}`}>{zh ? "恢复自动" : "reset"}</button>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              {!d.canEdit && <span className="text-[10px] text-slate-400 mr-auto">{zh ? "只读（管理员 / 工程经理 / CEO 可编辑）" : "Read-only"}</span>}
              <Button size="sm" variant="outline" onClick={onClose}>{zh ? "关闭" : "Close"}</Button>
              <Button size="sm" disabled={busy || !d.canEdit || !dirty} onClick={save} className="bg-brand-500 hover:bg-brand-600 text-white" data-testid="trade-task-map-save">{zh ? "保存全局默认" : "Save"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
